import React from 'react'
import EventEmitterEx from '../pageBaseComponents/EventEmitterEx'
import makeStyles from '@mui/styles/makeStyles';
import {ColumnFlexContainer} from '../../../styledComponents/FlexComponents'
import {Colors} from '../../../styledComponents/Constants'
import {BoldSpan} from '../../../styledComponents/StyledSpan'
import {CircularProgress, Backdrop, Box, Theme} from '@mui/material'

interface ExternalProps {
    eventEmitter: EventEmitterEx
}

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

export const withLoadingSpinner = <OriginalProps extends {}>(
    WrappedComponent: React.ComponentType<OriginalProps>,
) => {

    type ResultProps = OriginalProps & ExternalProps;

    interface LoadingSpinnerState {
        openCount: number,
        message: string,
    }

    return class extends React.Component<ResultProps, LoadingSpinnerState> {
        static displayName: string = `withLoadingSpinner(${WrappedComponent.displayName})`

        constructor(props: Readonly<OriginalProps & ExternalProps>) {
            super(props)
            this.state = {
                openCount: 0,
                message: 'Loading...',
            }

            this.handleShowLoading = this.handleShowLoading.bind(this)
            this.handleChangeLoadingMessage = this.handleChangeLoadingMessage.bind(this)
            this.handleCloseLoading = this.handleCloseLoading.bind(this)
        }

        componentDidMount(): void {
            this.props.eventEmitter.on('showLoading', this.handleShowLoading)
            this.props.eventEmitter.on('changeLoadingMessage', this.handleChangeLoadingMessage)
            this.props.eventEmitter.on('closeLoading', this.handleCloseLoading)
        }

        componentWillUnmount(): void {
            this.props.eventEmitter.off('showLoading', this.handleShowLoading)
            this.props.eventEmitter.off('changeLoadingMessage', this.handleChangeLoadingMessage)
            this.props.eventEmitter.off('closeLoading', this.handleCloseLoading)
        }

        handleShowLoading(message?: string): void {
            this.setState((prevState) => {
                return {
                    openCount: prevState.openCount + 1,
                    message: message ?? 'Loading...',
                }
            })
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur()
            }
        }

        handleChangeLoadingMessage(message: string): void {
            this.setState({message: message})
        }

        handleCloseLoading(): void {
            this.setState((prevState) => {
                if (prevState.openCount > 0) {
                    return {
                        openCount: prevState.openCount - 1,
                        message: prevState.message,
                    }
                }
            })
        }

        render(): JSX.Element {
            return <>
                <LoadingSpinner
                    open={this.state.openCount > 0}
                    message={this.state.message}
                />
                <WrappedComponent
                    {...this.props}
                />
            </>
        }
    }
}
const useStyles = makeStyles(theme => ({
    backdrop: {
        // zIndex: theme.zIndex.modal + 1,
        // TODO jestが認識できないため固定値とした
        zIndex: zIndexOfModal + 1,
        backgroundColor: 'rgba(241, 241, 241, 0.5)',
        color: `${Colors.AccentDark}`,
    },
}))

const zIndexOfModal: number = 1300

const LoadingSpinner: React.FC<{ open: boolean, message: string }> = props => {
    const classes = useStyles()
    return (<Backdrop className={classes.backdrop} open={props.open}>
        <ColumnFlexContainer>
            {
                props.open &&
                <>
                    <CircularProgress color={'inherit'}/>
                    <Box mt={'5px'}><BoldSpan>{props.message}</BoldSpan></Box>
                </>
            }
        </ColumnFlexContainer>
    </Backdrop>)
}
